/**
 * frenet-shipping-calculator.js
 *
 * Widget de cotacao de frete via API do Shopify (/cart/shipping_rates.json)
 * Compativel com: Carrinho Drawer + Pagina do Produto
 * Tema alvo: Trade 15.4.1
 */

(function () {
  'use strict';

  // ── CONFIGURACOES ─────────────────────────────────────────────────────────────
  var CONFIG = {
    cacheTTL: 10 * 60 * 1000,
    messages: {
      invalidCep: 'Informe um CEP valido (ex: 01310-100).',
      noRates: 'Nenhuma opcao de frete encontrada para este CEP.',
      apiError: 'Nao foi possivel calcular o frete. Tente novamente.',
      emptyCart: 'Adicione um produto ao carrinho para calcular o frete.',
      free: 'Gratis',
    },
    toggleText: {
      show: 'Ver todas as opcoes',
      hide: 'Ocultar opcoes',
    },
  };

  // ── CACHE ─────────────────────────────────────────────────────────────────────
  var ShippingCache = {
    _store: {},
    set: function (cep, data) {
      this._store[cep] = { data: data, timestamp: Date.now() };
    },
    get: function (cep) {
      var entry = this._store[cep];
      if (!entry) return null;
      if (Date.now() - entry.timestamp > CONFIG.cacheTTL) {
        delete this._store[cep];
        return null;
      }
      return entry.data;
    },
  };

  // ── UTILITARIOS ───────────────────────────────────────────────────────────────
  function formatCep(value) {
    return value.replace(/\D/g, '').replace(/^(\d{5})(\d)/, '$1-$2').slice(0, 9);
  }

  function isValidCep(cep) {
    return /^\d{5}-\d{3}$/.test(cep);
  }

  function formatPrice(cents) {
    if (cents === 0) return CONFIG.messages.free;
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100);
  }

  function formatDeadline(days) {
    if (!days || days <= 0) return '';
    var plural = days > 1;
    return 'Prazo: ' + days + ' dia' + (plural ? 's' : '') + ' util' + (plural ? 'eis' : '');
  }

  function extractDeadlineDays(title) {
    var match = title.match(/(\d+(?:[.,]\d+)?)\s*dia/i);
    return match ? parseFloat(match[1].replace(',', '.')) : null;
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  // ── VIACEP ────────────────────────────────────────────────────────────────────
  var UF_TO_PROVINCE = {
    AC: 'Acre',                AP: 'Amapa',              AM: 'Amazonas',
    AL: 'Alagoas',             BA: 'Bahia',               CE: 'Ceara',
    DF: 'Distrito Federal',    ES: 'Espirito Santo',      GO: 'Goias',
    MA: 'Maranhao',            MT: 'Mato Grosso',         MS: 'Mato Grosso do Sul',
    MG: 'Minas Gerais',        PA: 'Para',                PB: 'Paraiba',
    PR: 'Parana',              PE: 'Pernambuco',          PI: 'Piaui',
    RJ: 'Rio de Janeiro',      RN: 'Rio Grande do Norte', RS: 'Rio Grande do Sul',
    RO: 'Rondonia',            RR: 'Roraima',             SC: 'Santa Catarina',
    SP: 'Sao Paulo',           SE: 'Sergipe',             TO: 'Tocantins',
  };

  async function fetchAddressByCep(cleanCep) {
    var response = await fetch('https://viacep.com.br/ws/' + cleanCep + '/json/');
    if (!response.ok) throw new Error(CONFIG.messages.invalidCep);
    var data = await response.json();
    if (data.erro) throw new Error(CONFIG.messages.invalidCep);
    var province = UF_TO_PROVINCE[data.uf] || data.uf;
    return { province: province, city: data.localidade, uf: data.uf };
  }

  // ── API SHOPIFY ───────────────────────────────────────────────────────────────
  async function fetchShippingRates(cep) {
    var cleanCep = cep.replace(/\D/g, '');

    var cartResponse = await fetch('/cart.js');
    var cart = await cartResponse.json();
    if (!cart || cart.item_count === 0) throw new Error(CONFIG.messages.emptyCart);

    var address = await fetchAddressByCep(cleanCep);

    var params = new URLSearchParams({
      'shipping_address[zip]': cleanCep,
      'shipping_address[country]': 'Brazil',
      'shipping_address[province]': address.province,
    });

    var response = await fetch('/cart/shipping_rates.json?' + params.toString(), {
      headers: { 'Content-Type': 'application/json' },
    });

    if (response.status === 422) throw new Error(CONFIG.messages.noRates);
    if (!response.ok) throw new Error(CONFIG.messages.apiError);

    var data = await response.json();
    return data.shipping_rates || [];
  }

  // ── ORDENACAO ─────────────────────────────────────────────────────────────────
  function sortRates(rates) {
    return rates.slice().sort(function (a, b) {
      return parseFloat(a.price) - parseFloat(b.price);
    });
  }

  function priceToCents(priceStr) {
    return Math.round(parseFloat(priceStr) * 100);
  }

  // ── RENDERIZACAO ──────────────────────────────────────────────────────────────
  function renderResults(widget, rates) {
    var sorted = sortRates(rates);
    var cheapest = sorted[0];
    var others = sorted.slice(1);

    // Preenche melhor opcao
    widget.querySelector('.frenet-widget__cheapest-name').textContent = cheapest.name;
    widget.querySelector('.frenet-widget__cheapest-price').textContent = formatPrice(priceToCents(cheapest.price));
    var days = extractDeadlineDays(cheapest.name) || cheapest.delivery_days;
    widget.querySelector('.frenet-widget__cheapest-deadline').textContent = formatDeadline(days);

    // FIX 1: esconde o bloco de erro antes de mostrar resultados
    widget.querySelector('.frenet-widget__error').hidden = true;

    // Exibe bloco de resultados
    widget.querySelector('.frenet-widget__results').hidden = false;

    var toggleBtn = widget.querySelector('.frenet-widget__toggle-more');
    var optionsList = widget.querySelector('.frenet-widget__options-list');

    if (others.length > 0) {
      // Popula a lista de outras opcoes
      optionsList.innerHTML = '';
      others.forEach(function (rate) {
        var li = document.createElement('li');
        li.className = 'frenet-widget__option-item';
        var rateDays = extractDeadlineDays(rate.name) || rate.delivery_days;
        li.innerHTML =
          '<div class="frenet-widget__option-info">' +
            '<span class="frenet-widget__option-name">' + escapeHtml(rate.name) + '</span>' +
            (rateDays ? '<span class="frenet-widget__option-deadline">' + formatDeadline(rateDays) + '</span>' : '') +
          '</div>' +
          '<span class="frenet-widget__option-price">' + formatPrice(priceToCents(rate.price)) + '</span>';
        optionsList.appendChild(li);
      });

      // FIX 2: lista sempre FECHADA ao renderizar novo resultado
      optionsList.hidden = true;
      toggleBtn.hidden = false;
      toggleBtn.setAttribute('aria-expanded', 'false');

      // Reseta texto e icone do toggle
      var textNode = Array.prototype.slice.call(toggleBtn.childNodes).find(function (n) {
        return n.nodeType === Node.TEXT_NODE;
      });
      if (textNode) textNode.textContent = CONFIG.toggleText.show;
      var icon = toggleBtn.querySelector('svg');
      if (icon) {
        icon.style.transition = 'none';
        icon.style.transform = 'rotate(0deg)';
      }
    } else {
      toggleBtn.hidden = true;
      optionsList.hidden = true;
    }
  }

  // ── ESTADO ────────────────────────────────────────────────────────────────────
  function setLoading(widget, isLoading) {
    var btn = widget.querySelector('.frenet-widget__btn');
    var input = widget.querySelector('.frenet-widget__cep-input');
    widget.querySelector('.frenet-widget__btn-text').hidden = isLoading;
    widget.querySelector('.frenet-widget__btn-loader').hidden = !isLoading;
    btn.disabled = isLoading;
    input.disabled = isLoading;
  }

  function showError(widget, message) {
    var errorBlock = widget.querySelector('.frenet-widget__error');
    widget.querySelector('.frenet-widget__error-text').textContent = message;
    errorBlock.hidden = false;
    widget.querySelector('.frenet-widget__results').hidden = true;
  }

  function hideError(widget) {
    widget.querySelector('.frenet-widget__error').hidden = true;
  }

  function showCepError(widget, message) {
    var el = widget.querySelector('.frenet-widget__cep-error');
    el.textContent = message;
    el.hidden = false;
  }

  function hideCepError(widget) {
    widget.querySelector('.frenet-widget__cep-error').hidden = true;
  }

  // ── INICIALIZACAO DO WIDGET ───────────────────────────────────────────────────
  function initWidget(widget) {
    var input = widget.querySelector('.frenet-widget__cep-input');
    var btn = widget.querySelector('.frenet-widget__btn');
    var toggleBtn = widget.querySelector('.frenet-widget__toggle-more');
    var optionsList = widget.querySelector('.frenet-widget__options-list');

    // Mascara de CEP
    input.addEventListener('input', function (e) {
      e.target.value = formatCep(e.target.value);
      hideCepError(widget);
      hideError(widget);
    });

    // Enter para calcular
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') btn.click();
    });

    // Botao calcular
    btn.addEventListener('click', async function () {
      var cep = input.value.trim();

      if (!isValidCep(cep)) {
        showCepError(widget, CONFIG.messages.invalidCep);
        input.focus();
        return;
      }

      hideCepError(widget);
      hideError(widget);

      var cached = ShippingCache.get(cep);
      if (cached) {
        renderResults(widget, cached);
        return;
      }

      setLoading(widget, true);

      try {
        var rates = await fetchShippingRates(cep);
        if (!rates.length) {
          showError(widget, CONFIG.messages.noRates);
          return;
        }
        ShippingCache.set(cep, rates);
        renderResults(widget, rates);
      } catch (err) {
        showError(widget, err.message || CONFIG.messages.apiError);
        console.error('[FrenetWidget] Erro:', err);
      } finally {
        setLoading(widget, false);
      }
    });

    // Toggle de opcoes adicionais
    toggleBtn.addEventListener('click', function () {
      var isExpanded = toggleBtn.getAttribute('aria-expanded') === 'true';
      var willExpand = !isExpanded;

      toggleBtn.setAttribute('aria-expanded', String(willExpand));
      optionsList.hidden = !willExpand;

      var textNode = Array.prototype.slice.call(toggleBtn.childNodes).find(function (n) {
        return n.nodeType === Node.TEXT_NODE;
      });
      if (textNode) textNode.textContent = willExpand ? CONFIG.toggleText.hide : CONFIG.toggleText.show;

      var icon = toggleBtn.querySelector('svg');
      if (icon) {
        icon.style.transition = 'transform 0.2s ease';
        icon.style.transform = willExpand ? 'rotate(180deg)' : 'rotate(0deg)';
      }
    });
  }

  // ── OBSERVER PARA O DRAWER ────────────────────────────────────────────────────
  function observeDrawer() {
    var selectors = [
      '[id*="cart-drawer"]',
      '[class*="cart-drawer"]',
      '[id*="CartDrawer"]',
      'cart-drawer',
    ];
    var drawer = document.querySelector(selectors.join(','));
    if (!drawer) return;

    var observer = new MutationObserver(function () {
      var widget = drawer.querySelector('.frenet-shipping-widget');
      if (widget && !widget.dataset.initialized) {
        widget.dataset.initialized = 'true';
        initWidget(widget);
      }
    });

    observer.observe(drawer, { attributes: true, childList: true, subtree: true });
  }

  // ── BOOTSTRAP ─────────────────────────────────────────────────────────────────
  function init() {
    document.querySelectorAll('.frenet-shipping-widget').forEach(function (widget) {
      if (!widget.dataset.initialized) {
        widget.dataset.initialized = 'true';
        initWidget(widget);
      }
    });
    observeDrawer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();